import { Button } from "@/components/ui/button";
import {
  BookmarkPlus,
  Check,
  ChevronLeft,
  ChevronRight,
  CreditCard,
} from "lucide-react";
import { toArabicNumber } from "@/data/quranMeta";

/**
 * Flashcard reading mode — focuses on a single ayah at a time with
 * progress dots, optional word-by-word, transliteration, and translation.
 * All state and handlers are provided by the parent Reader component.
 */
export default function FlashcardView({
  displayAyahs,
  currentAyahIndex,
  setCurrentAyahIndex,
  surahNumber,
  surah,
  wbwCache,
  playAyah,
  isPlaying,
  handleDragStart,
  addBookmark,
  toggleMemorized,
  isMemorized,
  stackMode,
  stackLevel,
  showWordByWord,
  wbwLoading,
  showTafsir,
  tafsirEdition,
  tafsirCache,
  tafsirLoading,
  showTransliteration,
  showTranslation,
  getTransliterationText,
  getTranslationText,
  fontPreset,
  reviewMode,
}) {
  const currentDisplayAyah = displayAyahs[currentAyahIndex] || displayAyahs[0];

  if (!currentDisplayAyah) {
    return (
      <div className="glass-card rounded-xl p-8 text-center">
        <CreditCard className="w-12 h-12 text-gold-400/50 mx-auto mb-4" />
        <h3 className="font-cinzel text-lg text-slate-200 mb-2">No Ayahs</h3>
        <p className="text-slate-500 font-outfit text-sm">
          {reviewMode ? "No memorized ayahs to review" : "No ayahs available"}
        </p>
      </div>
    );
  }

  const surahNum = parseInt(surahNumber);
  const surahName = surah?.englishName;
  const wbwKey = `${surahNum}:${currentDisplayAyah.numberInSurah}`;
  const wbwWords = wbwCache[wbwKey];

  return (
    <div className="max-w-2xl mx-auto" data-testid="flashcard-view">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-2 mb-6">
        {displayAyahs.map((a, idx) => (
          <button
            key={`dot-${a.numberInSurah}`}
            onClick={() => {
              setCurrentAyahIndex(idx);
              const ayah = displayAyahs[idx];
              playAyah(surahNum, ayah.numberInSurah, idx);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentAyahIndex ? "bg-gold-400 w-6" : "bg-slate-600 hover:bg-slate-500"
            }`}
          />
        ))}
      </div>

      {/* Flashcard */}
      <div
        className={`glass-card rounded-2xl p-8 transition-all duration-300 ${
          isPlaying ? "border-gold-400/50 gold-glow" : ""
        }`}
        draggable
        onDragStart={(e) => handleDragStart(e, currentDisplayAyah, surahNum)}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div
              className="flex items-center gap-2 pr-2 pl-3 py-1 rounded-full"
              style={{
                background: "linear-gradient(180deg, #1B4D3E 0%, #0D2818 100%)",
                border: "1px solid rgba(201,162,39,0.45)",
                boxShadow: "0 0 18px rgba(201,162,39,0.08)",
              }}
            >
              <span className="text-[#E8D5A3] font-cinzel text-sm tracking-wide">{surahName}</span>
              <span
                className="inline-flex items-center justify-center w-7 h-7 select-none"
                style={{
                  fontFamily: "'Amiri', serif",
                  fontSize: "0.85rem",
                  fontWeight: 700,
                  background: "radial-gradient(circle, #FBF6E9 0%, #F5E9C3 70%, #E8D5A3 100%)",
                  border: "1.5px solid #C9A227",
                  borderRadius: "50%",
                  color: "#1B4D3E",
                  boxShadow: "inset 0 0 0 1px #C9A227, inset 0 1px 2px rgba(255,255,255,0.6)",
                }}
              >
                {toArabicNumber(currentDisplayAyah.numberInSurah)}
              </span>
            </div>
            {stackMode && (
              <span className="px-3 py-1 rounded-full bg-emerald-500/20 text-emerald-400 text-sm font-outfit">
                Stack {stackLevel}
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="bookmark-plus-btn"
              onClick={() => addBookmark(surahNum, currentDisplayAyah.numberInSurah)}
              className="w-10 h-10 rounded-full hover:bg-gold-400/10 flex items-center justify-center text-slate-400 hover:text-gold-400"
            >
              <BookmarkPlus className="w-5 h-5" />
            </button>
            <button
              onClick={() => toggleMemorized(surahNum, currentDisplayAyah.numberInSurah)}
              className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                isMemorized(surahNum, currentDisplayAyah.numberInSurah)
                  ? "bg-emerald-500 text-white"
                  : "bg-islamic-blue-800 text-slate-400 hover:text-emerald-400"
              }`}
            >
              <Check className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Arabic Text */}
        <div className="text-center py-8">
          <p
            className="font-noto-naskh text-slate-100 leading-[2.5]"
            style={{ direction: "rtl", fontSize: `${fontPreset.flashPx}px` }}
          >
            {currentDisplayAyah.text}
          </p>
        </div>

        {/* Word-by-Word */}
        {showWordByWord && (
          <div data-testid="wbw-block" className="border-t border-white/10 pt-6 mb-4">
            {wbwLoading && !wbwWords ? (
              <div className="flex items-center justify-center py-6">
                <div className="spinner w-6 h-6" />
              </div>
            ) : wbwWords && wbwWords.length > 0 ? (
              <div
                className="flex flex-wrap gap-3 justify-center"
                style={{ direction: "rtl" }}
                data-testid="wbw-words"
              >
                {wbwWords.map((w, i) => (
                  <div
                    key={`wbw-${i}-${w.arabic}`}
                    className="bg-islamic-blue-800/40 rounded-lg px-3 py-2 text-center min-w-[80px] hover:bg-gold-400/10 transition-colors"
                  >
                    <p className="font-noto-naskh text-2xl text-slate-100">{w.arabic}</p>
                    {w.transliteration && (
                      <p
                        className="text-gold-400/70 text-xs italic font-outfit mt-1"
                        style={{ direction: "ltr" }}
                      >
                        {w.transliteration}
                      </p>
                    )}
                    <p
                      className="text-slate-400 text-xs font-outfit mt-1"
                      style={{ direction: "ltr" }}
                    >
                      {w.translation}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-slate-500 text-sm font-outfit">
                Word-by-word unavailable for this verse
              </p>
            )}
          </div>
        )}

        {/* Transliteration */}
        {showTransliteration && (
          <p className="text-gold-400/80 font-outfit text-center text-lg italic mb-4">
            {getTransliterationText(currentDisplayAyah.numberInSurah)}
          </p>
        )}

        {/* Translation */}
        {showTranslation && (
          <div className="border-t border-white/10 pt-6">
            <p className="text-slate-400 font-outfit text-center text-lg leading-relaxed">
              {getTranslationText(surahNum, currentDisplayAyah.numberInSurah)}
            </p>
          </div>
        )}

        {/* Tafsir (commentary) */}
        {showTafsir && (() => {
          const tKey = `${tafsirEdition}|${surahNum}:${currentDisplayAyah.numberInSurah}`;
          const tEntry = tafsirCache?.[tKey];
          const isArabic = (tafsirEdition || "").startsWith("ar.");
          return (
            <div
              data-testid="tafsir-block"
              className="border-t border-white/10 pt-6 mt-2"
            >
              <div className="flex items-center justify-between mb-3">
                <span className="text-gold-400/80 font-cinzel text-sm tracking-wider uppercase">
                  Tafsir
                </span>
                {tEntry?.english_name && (
                  <span
                    className="text-slate-500 font-outfit text-xs"
                    data-testid="tafsir-edition-name"
                  >
                    {tEntry.english_name}
                  </span>
                )}
              </div>
              {tafsirLoading && !tEntry ? (
                <div className="flex items-center justify-center py-6">
                  <div className="spinner w-6 h-6" />
                </div>
              ) : tEntry?.text ? (
                <p
                  className={
                    isArabic
                      ? "font-noto-naskh text-slate-200 text-right text-xl leading-[2.2] whitespace-pre-line"
                      : "text-slate-300 font-outfit text-base leading-relaxed whitespace-pre-line"
                  }
                  style={isArabic ? { direction: "rtl" } : undefined}
                  data-testid="tafsir-text"
                >
                  {tEntry.text}
                </p>
              ) : (
                <p className="text-center text-slate-500 text-sm font-outfit">
                  Tafsir unavailable for this verse
                </p>
              )}
            </div>
          );
        })()}
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between mt-6">
        <Button
          variant="ghost"
          onClick={() => {
            if (currentAyahIndex > 0) {
              const newIdx = currentAyahIndex - 1;
              setCurrentAyahIndex(newIdx);
              const ayah = displayAyahs[newIdx];
              playAyah(surahNum, ayah.numberInSurah, newIdx);
            }
          }}
          disabled={currentAyahIndex <= 0}
          className="text-slate-400 hover:text-gold-400"
        >
          <ChevronRight className="w-6 h-6 mr-2" />
          Previous
        </Button>

        <span className="text-slate-500 font-outfit">
          {currentAyahIndex + 1} / {displayAyahs.length}
        </span>

        <Button
          variant="ghost"
          onClick={() => {
            if (currentAyahIndex < displayAyahs.length - 1) {
              const newIdx = currentAyahIndex + 1;
              setCurrentAyahIndex(newIdx);
              const ayah = displayAyahs[newIdx];
              playAyah(surahNum, ayah.numberInSurah, newIdx);
            }
          }}
          disabled={currentAyahIndex >= displayAyahs.length - 1}
          className="text-slate-400 hover:text-gold-400"
        >
          Next
          <ChevronLeft className="w-6 h-6 ml-2" />
        </Button>
      </div>
    </div>
  );
}
